import { PaginationMetaDto } from '@bondsports/types';
import { SetStateAction, useCallback, useRef, useState } from 'react';
import { FetchDataCallback, TFetchOptions } from '../types/fetchData';
import { isErrorResponse } from '../lib/utils';
import { useIntersectionObserver } from './useIntersectionObserver';
import { initialMeta as defaultInitialMeta } from '../lib/pagination';

const resolveItemsPerPage = (initialItemsPerPage?: number, initialMeta?: PaginationMetaDto): number =>
	initialItemsPerPage ?? initialMeta?.itemsPerPage ?? 0;

const buildInitialMeta = (initialItemsPerPage?: number, initialMeta?: PaginationMetaDto): PaginationMetaDto =>
	initialMeta ?? {
		...defaultInitialMeta,
		itemsPerPage: resolveItemsPerPage(initialItemsPerPage),
	};

export const useInfiniteScroll = <T, F>({
	fetchCallback,
	initialItemsPerPage,
	fetchOptions,
	initialItems,
	initialMeta,
}: {
	fetchCallback: FetchDataCallback<T, F>;
	initialItemsPerPage?: number;
	fetchOptions?: TFetchOptions<T>;
	initialItems?: T[];
	initialMeta?: PaginationMetaDto;
}) => {
	const [items, setItems] = useState<T[]>(initialItems || []);
	const [meta, setMeta] = useState<PaginationMetaDto>(buildInitialMeta(initialItemsPerPage, initialMeta));
	const initialHasMore = initialMeta ? initialMeta.currentPage < initialMeta.totalPages : true;
	const [hasMore, setHasMore] = useState(initialHasMore);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string>('');
	const filtersRef = useRef<F | undefined>(undefined);
	const isLoadingRef = useRef(false);
	const requestIdRef = useRef(0);
	const itemsRef = useRef<T[]>(initialItems || []);
	const metaRef = useRef<PaginationMetaDto>(buildInitialMeta(initialItemsPerPage, initialMeta));
	const hasMoreRef = useRef(initialHasMore);

	const setItemsAndRef = useCallback((value: SetStateAction<T[]>) => {
		const next = typeof value === 'function' ? value(itemsRef.current) : value;
		itemsRef.current = next;
		setItems(next);
	}, []);

	const setHasMoreAndRef = useCallback((value: SetStateAction<boolean>) => {
		const next = typeof value === 'function' ? value(hasMoreRef.current) : value;
		hasMoreRef.current = next;
		setHasMore(next);
	}, []);

	const resetState = () => {
		requestIdRef.current += 1;
		isLoadingRef.current = false;
		itemsRef.current = [];
		metaRef.current = {
			...defaultInitialMeta,
			itemsPerPage: resolveItemsPerPage(initialItemsPerPage, initialMeta),
		};
		hasMoreRef.current = true;
		filtersRef.current = undefined;
		setItems([]);
		setMeta(metaRef.current);
		setHasMore(true);
		setIsLoading(false);
		setError('');
	};

	const loadItems = async (append = true, filters?: F) => {
		if (append && isLoadingRef.current) {
			return;
		}

		if (filters !== undefined) {
			filtersRef.current = filters;
		} else if (!append) {
			filtersRef.current = undefined;
		}

		const activeFilters = filters !== undefined ? filters : filtersRef.current;
		const requestId = ++requestIdRef.current;
		isLoadingRef.current = true;
		setIsLoading(true);

		try {
			const nextPage = append ? metaRef.current.currentPage + 1 : 1;
			const response = await fetchCallback({
				page: nextPage,
				itemsPerPage: metaRef.current.itemsPerPage,
				filters: activeFilters,
			});

			if (requestId !== requestIdRef.current) {
				return;
			}

			if (!response) {
				return;
			}

			if (isErrorResponse(response)) {
				throw response;
			}

			fetchOptions?.onSuccess?.(response);
			setError('');
			metaRef.current = { ...response.meta, currentPage: nextPage };
			setMeta(metaRef.current);
			hasMoreRef.current = nextPage < response.meta.totalPages;
			setHasMore(hasMoreRef.current);
			itemsRef.current = append ? [...itemsRef.current, ...response.items] : response.items;
			setItems(itemsRef.current);

			return response;
		} catch (caught) {
			if (requestId !== requestIdRef.current) {
				return;
			}
			hasMoreRef.current = false;
			setHasMore(false);
			fetchOptions?.onError?.(caught);
			setError(isErrorResponse(caught) ? caught.err : caught instanceof Error ? caught.message : String(caught));
		} finally {
			if (requestId === requestIdRef.current) {
				isLoadingRef.current = false;
				setIsLoading(false);
				fetchOptions?.onComplete?.();
			}
		}
	};

	const refetch = async (newFilters?: F) => {
		resetState();
		await loadItems(false, newFilters);
	};

	const { observe } = useIntersectionObserver(async entries => {
		const firstEntry = entries?.[0];
		if (firstEntry?.isIntersecting && hasMoreRef.current && !isLoadingRef.current) {
			await loadItems(true);
		}
	});

	return {
		items,
		setItems: setItemsAndRef,
		refetch,
		meta,
		hasMore,
		isLoading,
		error,
		lastItemElementRef: observe,
		loadItems,
		resetState,
		setHasMore: setHasMoreAndRef,
	};
};
